import _ from 'underscore';
import { useEffect, useCallback, useContext, useState, useMemo } from 'react';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import { readCampaigns as readPmaxCampaigns } from '@bingads-webui-campaign-react/perf-max-data';
import { readCampaigns, fetchAssetGroupsData, getCampaignType, readAdGroups } from '@bingads-webui-campaign-react/pmax-lite-data';
import { redirectToLogin } from '@bingads-webui-campaign/global-app-init';
import { CAMPAIGN_TYPE } from '@bingads-webui-campaign-react/pmax-lite-wizard-utils';

export function useInitializeScope({ isGoalBasedSignup = false, selectedCampaignType } = {}) {
  // Ids will come from scope when refreshing
  const { scope } = useContext(ScopeContext);
  const {
    values: {
      cid,
      aid,
      campaignId: campaignIdFromScope,
      assetGroupId: assetGroupIdFromScope,
      adGroupId: adGroupIdFromScope,
    },
  } = scope;

  const serverContext = useContext(ServerContext);
  const hasAccount = !!(serverContext && serverContext.CurrentAccount);

  const instrumentation = useContext(InstrumentationContext);
  const uiActivity = useMemo(() => {
    if (instrumentation && instrumentation.pmaxLiteSignup) {
      const scenario = instrumentation.pmaxLiteSignup.create();
      return scenario.scope.create();
    }

    return { trace: _.noop, error: _.noop };
  }, [instrumentation]);

  const [scopeInitialized, setScopeInitialized] = useState(!hasAccount);
  const [isFetching, setIsFetching] = useState(false);
  const [isDataFetched, setIsDataFetched] = useState(false);

  const [campaignIdFromAPI, setCampaignIdFromAPI] = useState();
  const [assetGroupIdFromAPI, setAssetGroupIdFromAPI] = useState();
  const [campaignTypeFromAPI, setCampaignTypeFromAPI] = useState();
  const [adGroupIdFromAPI, setAdGroupIdFromAPI] = useState();
  const [languagesFromAPI, setLanguagesFromAPI] = useState();

  const onEntityReadError = useCallback((error) => {
    if (error && error.status === 401) {
      redirectToLogin(serverContext);
    }
  }, [serverContext]);

  useEffect(() => {
    if (hasAccount && !scopeInitialized && !isDataFetched && !isFetching) {
      setIsFetching(true);
      const readCampaignsFunc = isGoalBasedSignup ? readCampaigns : readPmaxCampaigns;
      readCampaignsFunc(cid, aid).then((res) => {
        let matchedCampaign = _.first(res.value);
        let campaignId = _.get(matchedCampaign, 'Id');
        let campaignType = getCampaignType(matchedCampaign);

        // If selectedCampaignType is provided, try to find the first campaign matching the type
        if (selectedCampaignType && campaignType !== selectedCampaignType) {
          matchedCampaign = _.find(res.value, campaign => getCampaignType(campaign) === selectedCampaignType);
          if (matchedCampaign) {
            campaignId = _.get(matchedCampaign, 'Id');
            campaignType = getCampaignType(matchedCampaign);
          } else {
            // No campaign of the expected type — treat the mismatched one as dirty data
            matchedCampaign = undefined;
            campaignId = undefined;
            campaignType = undefined;
          }
        }

        setCampaignIdFromAPI(campaignId);
        setCampaignTypeFromAPI(campaignType);

        if (isGoalBasedSignup) {
          const languages = _.get(matchedCampaign, 'Languages');
          setLanguagesFromAPI(languages);
        }

        if (!campaignId) {
          return Promise.resolve();
        }

        if (campaignType === CAMPAIGN_TYPE.SEARCH) {
          return readAdGroups(cid, aid, campaignId)
            .then((adGroupRes) => {
              const firstAdGroup = _.first(adGroupRes.value);
              const adGroupId = _.get(firstAdGroup, 'Id');
              setAdGroupIdFromAPI(adGroupId);
            });
        }

        return fetchAssetGroupsData({
          cid,
          aid,
          campaignId,
        }).then((assetGroupRes) => {
          if (assetGroupRes) {
            const firstAssetGroup = _.first(assetGroupRes.value);
            // In signup flow, we will only have one asset group
            const assetGroupId = _.get(firstAssetGroup, 'Id');
            setAssetGroupIdFromAPI(assetGroupId);
          }
        });
      }).catch(onEntityReadError)
        .finally(() => {
          setIsDataFetched(true);
          setIsFetching(false);
        });
    }
  }, [adGroupIdFromScope, aid, campaignTypeFromAPI, cid, hasAccount, isDataFetched,
    isFetching, onEntityReadError, scopeInitialized, isGoalBasedSignup, selectedCampaignType]);

  useEffect(() => {
    if (!scopeInitialized && isDataFetched) {
      if (!campaignIdFromAPI) {
        // No campaign Id
        if (campaignIdFromScope || assetGroupIdFromScope || adGroupIdFromScope) {
          scope.remove(['campaignId', 'assetGroupId', 'adGroupId']);
          uiActivity.trace(`Remove campaignId ${campaignIdFromScope} or assetGroupId ${assetGroupIdFromScope} or adGroupId ${adGroupIdFromScope} from scope`);
        }
      } else if (campaignTypeFromAPI === CAMPAIGN_TYPE.SEARCH) {
        scope.append({ campaignId: campaignIdFromAPI });
        uiActivity.trace(`Save search campaignId ${campaignIdFromAPI} to scope`);
        if (!adGroupIdFromAPI && adGroupIdFromScope) {
          scope.remove(['adGroupId']);
          uiActivity.trace(`Remove adGroupId ${adGroupIdFromScope} from scope`);
        } else if (adGroupIdFromAPI) {
          scope.append({ adGroupId: adGroupIdFromAPI });
          uiActivity.trace(`Save adGroupId ${adGroupIdFromAPI} to scope`);
        }
      } else if (!assetGroupIdFromAPI) {
        // Campaign Id exists but no asset group Id
        scope.append({ campaignId: campaignIdFromAPI });
        uiActivity.trace(`Save campaignId ${campaignIdFromAPI} to scope`);
        if (assetGroupIdFromScope) {
          scope.remove(['assetGroupId']);
          uiActivity.trace(`Remove assetGroupId ${assetGroupIdFromScope} and save campaignId ${campaignIdFromAPI} to scope`);
        }
      } else {
        // Both campaignId and assetGroupId exist
        scope.append({ assetGroupId: assetGroupIdFromAPI });
        scope.append({ campaignId: campaignIdFromAPI });
        uiActivity.trace(`Save campaignId ${campaignIdFromAPI} and assetGroupId ${assetGroupIdFromAPI} to scope`);
      }
      uiActivity.trace(`Final scope search string: ${scope.stringify()}`);
      scope.save();
      setScopeInitialized(true);
    }
  }, [scopeInitialized, isDataFetched, campaignIdFromAPI, assetGroupIdFromAPI,
    uiActivity, scope, campaignIdFromScope, assetGroupIdFromScope, adGroupIdFromAPI,
    adGroupIdFromScope, campaignTypeFromAPI]);

  return {
    scopeInitialized,
    hasCampaign: !!campaignIdFromAPI,
    hasAssetGroup: !!assetGroupIdFromAPI,
    campaignId: campaignIdFromAPI,
    adGroupId: adGroupIdFromAPI,
    campaignType: campaignTypeFromAPI,
    languages: languagesFromAPI,
  };
}
